import type {FC, ForwardedRef, ReactNode, Ref} from 'react';
import React, {forwardRef, useEffect, useImperativeHandle, useRef, useState} from 'react';
import classNames from 'classnames';

import {showTooltip} from '@pb/kit-util-show-tooltip';
import {useCallbackWithRef} from '@pb/kit-util-use-callback-with-ref';


export type Props = {
    /**
     * Determine whether enable or disable ellipsis text
     */
    isActive?: boolean,

    /**
     * Custom text for hint
     */
    title?: string,

    /**
     * Children node
     */
    children: ReactNode,

    /**
     * ClassName for EllipsisTextHint
     */
    mix?: string,

    /**
     * Determine whether bold text
     */
    bold?: boolean,

    /**
     * Called with `trimmed` value after component update
     */
    checkIfTextIsTrimmed?: (trimmed: boolean) => void,
};

type EllipsisRef = {
    el: HTMLElement | null,
};

interface PropsWithRef extends Props {
    ref?: Ref<EllipsisRef>,
}

/**
 * @see [StoryBook]{@link https://storybook.dev.pitchbookdata.com/?path=/story/text-ellipsis-text-hint}
 * */
export const EllipsisTextHint = forwardRef((props: Props, ref: ForwardedRef<EllipsisRef>) => {
    const {
        mix,
        bold,
        isActive = true,
        title,
        children,
        checkIfTextIsTrimmed,
    } = props;

    const [trimmed, setTrimmed] = useState(false);

    const el = useRef<HTMLElement | null>(null);
    const observer = useRef<ResizeObserver | null>(null);

    useImperativeHandle(ref, () => ({
        el: el.current,
    }), [el.current]); // eslint-disable-line react-hooks/exhaustive-deps

    const removeTitleObserver = () => {
        observer.current?.disconnect();
    };

    const setTitle = useCallbackWithRef(() => {
        if (isActive) {
            const newTrimmed = showTooltip(el.current, title);

            if (trimmed !== newTrimmed) {
                setTrimmed(newTrimmed);
            }
        }
    });

    useEffect(() => {
        setTitle();
    }, [bold, children, setTitle, title]);

    useEffect(() => {
        if (checkIfTextIsTrimmed) {
            checkIfTextIsTrimmed(trimmed);
        }
    }, [trimmed]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (isActive && el.current) {
            observer.current = new ResizeObserver(setTitle);
            observer.current.observe(el.current);

            return removeTitleObserver;
        }
        return undefined;
    }, [isActive, setTitle, title]);

    const Tag = bold ? 'strong' : 'span';
    const classes = classNames(mix, {
        ellipsis: isActive,
    });
    return (
        <Tag className={classes} ref={el}>
            {children}
        </Tag>
    );
}) as FC<PropsWithRef>;

EllipsisTextHint.displayName = 'EllipsisTextHint';
